import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { useProperties } from '../queries/properties';

const today = () => dayjs().format('MMM D');
const lastWeek = () => `${dayjs().subtract(6, 'day').format('MMM D')} – ${dayjs().format('D')}`;

export const HOME_RANGES = [
  { key: 'all', label: 'All time', short: 'All' },
  { key: 'day', label: 'Current day', short: 'Today', hint: today },
  { key: '24h', label: 'Last 24h', short: '24h' },
  { key: '48h', label: 'Last 48h', short: '48h' },
  { key: '72h', label: 'Last 72h', short: '72h' },
  { key: '7d', label: 'Last 7d', short: '7d', hint: lastWeek }
];

const ALL_PROPERTIES = { key: 'all', label: 'All Properties' };

const unitsHint = (n) => `${n} ${n === 1 ? 'unit' : 'units'}`;

export function useScopeOptions() {
  const { rows } = useProperties();
  return useMemo(() => {
    const owners = new Map();
    rows.forEach((l) => l.ownerId && owners.set(l.ownerId, l.ownerName || 'Owner'));
    const option = (key, label, match) => ({ key, label, hint: unitsHint(rows.filter(match).length) });
    return [
      { label: 'Portfolio', items: [option(ALL_PROPERTIES.key, ALL_PROPERTIES.label, () => true)] },
      {
        label: 'By use',
        showLabel: true,
        items: [
          option('residential', 'Residential', (l) => l.use === 'residential'),
          option('commercial', 'Commercial', (l) => l.use === 'commercial')
        ]
      },
      {
        label: 'Custom groups',
        showLabel: owners.size > 0,
        items: [...owners].map(([id, name]) => option(`owner:${id}`, name, (l) => l.ownerId === id))
      }
    ];
  }, [rows]);
}

export function useHomeScope(defaultRange = 'all') {
  const [search, setSearch] = useSearchParams();
  const groups = useScopeOptions();
  const range = search.get('range') || defaultRange;
  const scope = search.get('scope') || 'all';

  return useMemo(() => {
    const win = HOME_RANGES.find((r) => r.key === range) || HOME_RANGES[0];
    const market = groups.flatMap((g) => g.items).find((m) => m.key === scope) || ALL_PROPERTIES;
    const update = (patch) =>
      setSearch(
        (prev) => {
          const next = new URLSearchParams(prev);
          Object.entries(patch).forEach(([k, v]) => (v ? next.set(k, v) : next.delete(k)));
          return next;
        },
        { replace: true }
      );
    return {
      win,
      market,
      groups,
      params: { ...(win.key !== 'all' && { range: win.key }), ...(scope !== 'all' && { scope }) },
      active: search.has('range') || search.has('scope'),
      note: `${win.label} · ${market.label}`,
      setRange: (key) => update({ range: key }),
      setScope: (key) => update({ scope: key }),
      clear: () => update({ range: null, scope: null })
    };
  }, [range, scope, groups, search, setSearch]);
}
